import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ExercicesProvider } from '../../../exercices/providers/exercices-provider';
import { WeekService } from '../../providers/weeks.services';
import { Subscription } from 'rxjs';
import { Organization } from "../../../common/models/organization";
import { AnnualPlan } from "../../../common/models/annual-plan";
import { User } from "../../../common/models/user";
import { Week } from "../../../common/models/week";
import { ExerciceCategory } from "../../../common/models/exercice-category";
import { TranslateModule } from "@ngx-translate/core";
import { MatError, MatFormField, MatHint, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatSlider, MatSliderModule } from "@angular/material/slider";
import { NgForOf, NgIf } from "@angular/common";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

@Component({
  selector: 'app-annual-plan-form-week',
  templateUrl: './annual-plan-form-week.component.html',
  standalone: true,
  styleUrls: ['./annual-plan-form-week.scss'],
  imports: [
    TranslateModule,
    MatHint,
    MatFormField,
    MatError,
    MatLabel,
    MatSelectModule,
    MatSliderModule,
    NgIf,
    ReactiveFormsModule,
    CdkTextareaAutosize,
    MatInput,
    NgForOf,
    MatButton,
    FormsModule,
  ]
})
export class AnnualPlanFormWeekComponent implements OnInit, AfterViewInit, OnDestroy {

    public readonly DESCRIPTION_MAX_LENGTH: number = 250;

    @Input()
    public organization: Organization = {} as Organization;
    @Input()
    public annualPlan: AnnualPlan = {} as AnnualPlan;
    public currentUser: User = {} as User;
    public weekForm: FormGroup;
    public isSubmitting = false;
    public week: Week | undefined;
    public exerciseCategories: ExerciceCategory[] = [];
    @Output() public weekToUpdateEvent: EventEmitter<Week> = new EventEmitter<Week>();
    @Output() public weekFormEvent: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
    @Output() public toggleProTip: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    weeks: Week[] = [];

    @Input()
    set clickedWeek(clickedWeek: Week) {
        if (clickedWeek && clickedWeek.id) {
            this.week = clickedWeek;

            // patch "week form" with values of this.weeks because this is the array
            // containing updated values
            const index = this.weeks.findIndex((e) => e.id === clickedWeek.id);
            const week = this.weeks[index];

            // no trigger change event during patching value of the new clicked week...
            this.triggerChangeEvents = false;
            this.weekForm.patchValue(week);
            this.triggerChangeEvents = true;
        }
    }

    private subscriptions: Subscription[] = [];
    private triggerChangeEvents = false;

    constructor(
        private fb: FormBuilder,
        private exerciseProvider: ExercicesProvider,
        private weekProvider: WeekService,
        private cdRef: ChangeDetectorRef,
    ) {
        // use the FormBuilder to create a form group
        this.weekForm = this.fb.group({
            id: new FormControl(null),
            physicalPercent: new FormControl(0, [Validators.required]),
            sportPercent: new FormControl(0, [Validators.required]),
            intensityPercent: new FormControl(0, [Validators.required]),
            exerciseCategories: new FormControl(0, []),
            comment: new FormControl('', [Validators.maxLength(250)])
        });

        this.onWeekFormChanged();
    }


    ngAfterViewInit() {
      this.cdRef.detectChanges(); // Ajoutez cette ligne pour détecter les changements après l'initialisation de la vue
    }

    public onWeekFormChanged(): void {
        this.subscriptions.push(
            this.weekForm.valueChanges.subscribe((week) => {
                if (this.triggerChangeEvents) {

                    // Update weeks array with new form values
                    const index = this.weeks.findIndex((e) => e.id === week.id);
                    this.weeks[index] = Object.assign(this.weeks[index], week);

                    // send week to list to update
                    this.weekToUpdateEvent.emit(week);
                    this.weekFormEvent.emit(this.weekForm);
                }
            })
        );
    }

    public ngOnInit() {
        if (this.clickedWeek) {
            this.weekForm.patchValue(this.clickedWeek);
        }

        this.subscriptions.push(
            this.exerciseProvider.getExerciseCategories(this.organization).subscribe((categories) => {
                this.exerciseCategories = categories;
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    get f() {
        return this.weekForm.controls;
    }

    public formatLabel(value: number | null) {
        if (!value) {
            return '';
        }

        return value.toString();
    }

  public onSliderChange(event: any, formControlName: string) {
    const control = this.weekForm.get(formControlName) as FormControl;
    if (event && event.target) {
      control.setValue(event.target.value);
    } else if (event && event.value !== undefined) {
      control.setValue(event.value);
    }

    const week = this.weeks.find((x: any) => x.id === this.week?.id)
    if (!week) throw new Error('Week not found');
    (week as any)[formControlName] = control.value;

    const physicalPercent = Number(this.weekForm.get('physicalPercent')?.value);
    const sportPercent = Number(this.weekForm.get('sportPercent')?.value);
    const greaterThan100Percent = (physicalPercent + sportPercent) > 100;

    if ('physicalPercent' === formControlName && greaterThan100Percent) {
      this.weekForm.get('sportPercent')?.setValue(100 - this.weekForm.controls['physicalPercent'].value);
      week['sportPercent'] = 100 - this.weekForm.controls['physicalPercent'].value;
    }

    if ('sportPercent' === formControlName && greaterThan100Percent) {
      this.weekForm.get('physicalPercent')?.setValue(100 - this.weekForm.controls['sportPercent'].value);
      week['physicalPercent'] = 100 - this.weekForm.controls['sportPercent'].value;
    }
  }

    public copyPreviousWeekValues() {
        this.subscriptions.push(
            this.weekProvider.findPreviousByWeek(this.organization, this.annualPlan, this.week?.weekNumber!).subscribe((previousWeek) => {
                if (previousWeek) {
                    const index = this.weeks.findIndex((e) => e.id === previousWeek.id);
                    this.weekForm.patchValue({
                        id: this.week?.id,
                        intensityPercent: this.weeks[index].intensityPercent,
                        sportPercent: this.weeks[index].sportPercent,
                        physicalPercent: this.weeks[index].physicalPercent,
                        exerciseCategories: (this.weeks[index]as any)['exerciseCategories'],
                        comment: this.weeks[index].comment,
                    });
                }
            })
        );
    }

    public compareObjects(o1: any, o2: any): boolean {
        return o1.name === o2.name && o1._id === o2._id;
    }

    public disableProTip() {
        this.toggleProTip.emit(false);
    }

    public enableProTip() {
        this.toggleProTip.emit(true);
    }

}
